// Theme presets + custom theme derivation. Values are "r g b" (tokens) or "r, g, b" (section glows).
export const DEFAULT_LOOK = {
  theme: "cream", custom_themes: [], wallpaper: { type: "none", id: null }, dim: 40,
  screensaver: { enabled: true, idle_minutes: 10, mode: "photos" }, sound_muted: false,
};

const DARK_GLOWS = { home: "244, 121, 32", money: "34, 197, 94", kids: "236, 72, 153", calendar: "59, 130, 246", connect: "149, 106, 248", media: "239, 68, 68", family: "6, 182, 212", admin: "167, 139, 250" };
const BRIGHT_GLOWS = { home: "251, 146, 60", money: "74, 222, 128", kids: "244, 114, 182", calendar: "96, 165, 250", connect: "167, 139, 250", media: "248, 113, 113", family: "34, 211, 238", admin: "196, 181, 253" };
const LIGHT_GLOWS = { home: "194, 65, 12", money: "21, 128, 61", kids: "190, 24, 93", calendar: "29, 78, 216", connect: "109, 40, 217", media: "185, 28, 28", family: "14, 116, 144", admin: "91, 33, 182" };

// Cream: light warm default. Headings Queue Orange, small orange text deeper (accentText), deep section colours (4.5:1 even on their own tints).
const CREAM_GLOWS = { home: "154, 52, 18", money: "22, 101, 52", kids: "157, 23, 77", calendar: "29, 78, 216", connect: "109, 40, 217", media: "185, 28, 28",
  family: "21, 94, 117", admin: "91, 33, 182", lists: "146, 64, 14", copilot: "17, 94, 89" };

export const PRESETS = [
  { id: "cream", label: "Cream (Default)", scheme: "light", bg: "251 247 240", surface: "255 253 249", raised: "244 237 225", border: "232 223 207", line: "220 209 191",
    text: "20 20 20", muted: "94 85 74", faint: "111 102 90", wordmark: "20 20 20", accent: "244 121 32", accentInk: "13 13 13",
    accentText: "168 68 8", heading: "244 121 32", dangerText: "185 28 28", glows: CREAM_GLOWS, sideDark: true },
  { id: "dark", label: "Dark", scheme: "dark", bg: "13 13 13", surface: "18 18 18", raised: "24 24 27", border: "26 26 26", line: "39 39 42",
    text: "244 244 245", muted: "161 161 170", faint: "135 135 146", wordmark: "237 232 221", accent: "244 121 32", accentInk: "13 13 13",
    dangerText: "252 165 165", glows: DARK_GLOWS },
  { id: "light", label: "Light", scheme: "light", bg: "246 244 240", surface: "255 255 255", raised: "240 238 233", border: "228 225 218", line: "208 204 196",
    text: "24 24 27", muted: "72 72 82", faint: "92 92 102", wordmark: "24 24 27", accent: "194 65 12", accentInk: "255 255 255",
    dangerText: "185 28 28", glows: LIGHT_GLOWS },
  { id: "ocean", label: "Ocean", scheme: "dark", bg: "6 22 36", surface: "11 33 52", raised: "16 43 66", border: "21 52 78", line: "33 70 101",
    text: "232 244 250", muted: "158 192 211", faint: "134 170 192", wordmark: "232 244 250", accent: "56 189 248", accentInk: "6 22 36",
    dangerText: "252 165 165", glows: BRIGHT_GLOWS },
  { id: "forest", label: "Forest", scheme: "dark", bg: "11 24 17", surface: "17 36 26", raised: "23 46 33", border: "29 56 40", line: "42 74 54",
    text: "236 245 236", muted: "168 197 174", faint: "142 174 150", wordmark: "236 245 236", accent: "163 230 53", accentInk: "11 24 17",
    dangerText: "252 165 165", glows: { ...BRIGHT_GLOWS, money: "110, 231, 160" } },
  { id: "sunset", label: "Sunset", scheme: "dark", bg: "36 15 27", surface: "50 21 37", raised: "62 27 46", border: "74 34 55", line: "96 47 70",
    text: "255 241 234", muted: "232 190 180", faint: "208 164 156", wordmark: "255 241 234", accent: "251 146 60", accentInk: "36 15 27",
    dangerText: "254 202 202", glows: { ...BRIGHT_GLOWS, home: "253, 186, 116", media: "252, 129, 129" } },
  { id: "candy", label: "Candy", scheme: "light", bg: "255 240 247", surface: "255 255 255", raised: "252 231 243", border: "248 215 233", line: "238 190 218",
    text: "59 16 51", muted: "104 52 94", faint: "120 66 108", wordmark: "59 16 51", accent: "190 24 93", accentInk: "255 255 255",
    dangerText: "185 28 28", glows: { ...LIGHT_GLOWS, kids: "162, 28, 175" } },
];
export const PRESET_IDS = PRESETS.map((p) => p.id);

// ---------- colour maths ----------
export const isHex = (v) => /^#[0-9a-fA-F]{6}$/.test(v || "");
export const hexToRgb = (h) => [1, 3, 5].map((i) => parseInt(h.slice(i, i + 2), 16));
export const rgbToHex = (rgb) => "#" + rgb.map((v) => Math.round(v).toString(16).padStart(2, "0")).join("").toUpperCase();
const parse = (s) => s.split(/[ ,]+/).map(Number);
const str = (rgb) => rgb.map(Math.round).join(" ");
const mix = (a, b, t) => a.map((v, i) => v + (b[i] - v) * t);

function lum(rgb) {
  const [r, g, b] = rgb.map((v) => {
    const c = v / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}
export function contrast(a, b) {
  const [x, y] = [lum(a), lum(b)].sort((m, n) => n - m);
  return (x + 0.05) / (y + 0.05);
}

// Nudge the text colour toward white or black until it reads on both background and cards.
export function fixText(text, bg, cards) {
  const ok = (c) => contrast(c, bg) >= 4.5 && contrast(c, cards) >= 4.5;
  for (let t = 0.05; t <= 1.001; t += 0.05) {
    for (const end of [[255, 255, 255], [17, 17, 17]]) {
      const c = mix(text, end, t).map(Math.round);
      if (ok(c)) return c;
    }
  }
  // Background and cards are too far apart for one colour: make it pass on the background.
  return contrast([255, 255, 255], bg) >= contrast([17, 17, 17], bg) ? [255, 255, 255] : [17, 17, 17];
}

function rgbToHsl([r, g, b]) {
  r /= 255; g /= 255; b /= 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h = 0, s = 0;
  const l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    h = max === r ? (g - b) / d + (g < b ? 6 : 0) : max === g ? (b - r) / d + 2 : (r - g) / d + 4;
    h *= 60;
  }
  return `${Math.round(h)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

export function deriveCustom(ct) {
  const bg = hexToRgb(ct.background), surface = hexToRgb(ct.cards), text = hexToRgb(ct.text), accent = hexToRgb(ct.accent);
  const light = lum(bg) > 0.35;
  const ink = contrast(accent, [13, 13, 13]) >= contrast(accent, [255, 255, 255]) ? "13 13 13" : "255 255 255";
  return {
    id: `custom:${ct.id}`, label: ct.name, scheme: light ? "light" : "dark", bg: str(bg), surface: str(surface),
    raised: str(mix(surface, text, 0.05)), border: str(mix(bg, text, 0.1)), line: str(mix(bg, text, 0.18)),
    text: str(text), muted: str(mix(text, surface, 0.22)), faint: str(mix(text, surface, 0.32)), wordmark: str(text),
    accent: str(accent), accentInk: ink, dangerText: light ? "185 28 28" : "252 165 165", glows: light ? LIGHT_GLOWS : BRIGHT_GLOWS,
  };
}

export function themeOf(look) {
  const id = look?.theme || "cream";
  if (id.startsWith("custom:")) {
    const ct = (look.custom_themes || []).find((t) => `custom:${t.id}` === id);
    if (ct) return deriveCustom(ct);
  }
  return PRESETS.find((p) => p.id === id) || PRESETS[0];
}

// Hex quartet (background/cards/text/accent) for any theme, used to seed the editor.
export const quartetOf = (t) => ({ background: rgbToHex(parse(t.bg)), cards: rgbToHex(parse(t.surface)), text: rgbToHex(parse(t.text)), accent: rgbToHex(parse(t.accent)) });

function baseVars(t) {
  const v = {
    "--q-bg": t.bg, "--q-surface": t.surface, "--q-raised": t.raised, "--q-border": t.border, "--q-border-strong": t.line,
    "--q-text": t.text, "--q-muted": t.muted, "--q-faint": t.faint, "--q-wordmark": t.wordmark, "--q-accent": t.accent,
    "--q-accent-ink": t.accentInk, "--q-accent-text": t.accentText || t.accent, "--q-heading": t.heading || t.text,
    "--q-danger-text": t.dangerText, "--q-date-invert": t.scheme === "light" ? "0" : "0.8",
    "--background": rgbToHsl(parse(t.bg)), "--foreground": rgbToHsl(parse(t.text)), "--card": rgbToHsl(parse(t.surface)),
    "--card-foreground": rgbToHsl(parse(t.text)), "--popover": rgbToHsl(parse(t.surface)), "--popover-foreground": rgbToHsl(parse(t.text)),
    "--primary": rgbToHsl(parse(t.accent)), "--primary-foreground": rgbToHsl(parse(t.accentInk)), "--secondary": rgbToHsl(parse(t.raised)),
    "--secondary-foreground": rgbToHsl(parse(t.text)), "--muted": rgbToHsl(parse(t.raised)), "--muted-foreground": rgbToHsl(parse(t.muted)),
    "--accent": rgbToHsl(parse(t.raised)), "--accent-foreground": rgbToHsl(parse(t.text)), "--border": rgbToHsl(parse(t.border)),
    "--input": rgbToHsl(parse(t.line)), "--ring": rgbToHsl(parse(t.accent)),
  };
  v["--glow-lists"] = "245, 158, 11";
  v["--glow-copilot"] = "20, 184, 166";
  Object.entries(t.glows).forEach(([k, val]) => { v[`--glow-${k}`] = val; });
  return v;
}

// Sidebar ("chrome") tokens --qs-*: Cream pins them to today's Dark values; index.css maps them inside .q-chrome for Cream only.
export function themeVars(t) {
  const v = baseVars(t);
  if (t.sideDark) {
    Object.entries(baseVars(PRESETS.find((p) => p.id === "dark"))).forEach(([k, val]) => { v[`--qs-${k.slice(2)}`] = val; });
    v["--qs-scheme"] = "dark";
  }
  return v;
}

export const galleryUrl = (id) => `/wallpapers/${id}.webp`;
export const galleryThumb = (id) => `/wallpapers/${id}-thumb.webp`;
// v2 since Phase 39 (Cream default); index.html reads v2, else maps an old dark cache to Cream.
const VARS_KEY = "qhh.look.vars.v2";
const OLD_VARS_KEY = "qhh.look.vars";
let applied = [];

// Applies a look to <html> synchronously and caches it so index.html can paint it before React loads.
export function applyLook(look, mineUrl = null, { cache = true } = {}) {
  if (!look) return;
  const t = themeOf(look);
  const wp = look.wallpaper || {};
  const url = wp.type === "gallery" ? galleryUrl(wp.id) : wp.type === "mine" ? mineUrl : null;
  const vars = { ...themeVars(t), "--q-dim": String((look.dim ?? 40) / 100), "--q-wallpaper": url ? `url("${url}")` : "none" };
  const root = document.documentElement;
  Object.entries(vars).forEach(([k, val]) => root.style.setProperty(k, val));
  root.classList.toggle("dark", t.scheme === "dark");
  root.style.colorScheme = t.scheme;
  root.dataset.theme = t.id;
  applied = [...new Set([...applied, ...Object.keys(vars)])];
  if (!cache) return;
  const cached = { ...vars };
  if (wp.type === "mine") cached["--q-wallpaper"] = "none";
  localStorage.setItem(VARS_KEY, JSON.stringify(cached));
  localStorage.removeItem(OLD_VARS_KEY);
}

// Back to the built-in dark look (index.css defaults). Signed-out pages and pinned public pages use this; keepCache leaves the person's paint cache.
export function resetLook({ keepCache = false } = {}) {
  const root = document.documentElement;
  applied.forEach((k) => root.style.removeProperty(k));
  applied = [];
  ["--q-wallpaper", "--q-dim"].forEach((k) => root.style.removeProperty(k));
  [VARS_KEY, OLD_VARS_KEY].forEach((key) => Object.keys(JSON.parse(localStorage.getItem(key) || "{}")).forEach((k) => root.style.removeProperty(k)));
  root.classList.add("dark");
  root.style.colorScheme = "dark";
  delete root.dataset.theme;
  if (!keepCache) [VARS_KEY, OLD_VARS_KEY].forEach((k) => localStorage.removeItem(k));
}
